import { useCallback, useEffect } from 'react';
import { createStore } from '@frontend/store';

export type MicrophonePermissionState = PermissionState | 'unknown';

const useUserMediaPermissionStore = createStore<{
  microphone: MicrophonePermissionState;
  setMicrophone: (state: MicrophonePermissionState) => void;
}>(
  (set) => {
    const setMicrophone = (permission: MicrophonePermissionState) =>
      set(
        (state) => {
          state.microphone = permission;
        },
        false,
        'SET_MICROPHONE'
      );

    return {
      microphone: 'unknown',
      setMicrophone,
    };
  },
  { name: 'UserMediaPermissionStore', trace: true }
);

export const useMicrophonePermissionRequest = () => {
  const permission = useUserMediaPermissionStore((ctx) => ctx.microphone);
  const setPermission = useUserMediaPermissionStore((ctx) => ctx.setMicrophone);

  useEffect(() => {
    if (permission !== 'prompt') {
      return;
    }

    let isCancelled = false;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
        if (!isCancelled) {
          setPermission('granted');
        }
      })
      .catch((error: DOMException) => {
        if (isCancelled) {
          return;
        }

        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError' ||
          error.name === 'SecurityError'
        ) {
          setPermission('denied');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [permission, setPermission]);
};

export const useMicrophonePermissionState = () => {
  const permission = useUserMediaPermissionStore((ctx) => ctx.microphone);
  const setPermission = useUserMediaPermissionStore((ctx) => ctx.setMicrophone);

  const queryPermission = useCallback(async () => {
    if (navigator.permissions?.query) {
      try {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        setPermission(result.state);
        return;
      } catch {
        // Fall through to enumerateDevices when the Permissions API is unavailable or unsupported.
      }
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((device) => device.kind === 'audioinput');

    if (audioInputs.length === 0 && devices.length > 0) {
      setPermission('denied');
      return;
    }

    if (audioInputs.some((device) => device.label)) {
      setPermission('granted');
      return;
    }

    setPermission('prompt');
  }, [setPermission]);

  useEffect(() => {
    if (permission === 'unknown') {
      queryPermission();
    }
  }, [permission, queryPermission]);

  return {
    permission,
  };
};
